import React, { FormEventHandler } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import dayjs from 'dayjs'
import { type ApplicationResponse } from '@mobi/api-types/index'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Input, Checkbox } from '@mobi/component-library/Selection&Input/Inputs'
import { CustomerServiceEmail } from '@mobi/component-library/Base/Link'
import { spacing } from '@mobi/component-library/Foundations'
import { NameInput, AddressInput, Nationality, CountrySelectField } from '@mobi/account/Common'
import {
  getEmailValidationMessage,
  getMobileValidationMessage,
  isValidName,
  trackSignUpAlreadyHaveAccount,
  trackSignUpError,
  trackSignUpNameAndDOB,
  trackSignUpStart,
  isVerificationSuccessful,
  routes,
  ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT,
  getFormattedDateOfBirth,
  validateDateOfBirth,
} from '../Utils'
import {
  getSignUpState,
  setHasUserStartedSignup,
  setNumberOfAttemptsToVerify,
  setShowPreviousAddress,
  setSignupApplication,
  setSignupVerification,
  setUserKnownByOtherNames,
} from '../Store'
import {
  useApplication,
  useSignUpApplication,
  type TypeSignUpApplicationRequest,
  type TypeSignUpApplicationResponse,
} from '../Hooks'
import {
  SignupError,
  BoldStyled,
  Caption,
  CheckboxLabel,
  FooterStyled,
  FormSection,
  Legend,
  PageLayout,
  PersonalDetailsDisclaimer,
} from './'
import { Contract } from '@mobi/web-native-comms'
import { FeatureFlags } from '@mobi/settings'
import { useHostContextFeature } from '@mobi/account/Hooks'
import { trackVerificationSuccess } from '../../Verification/Utils/ga'
import {
  duplicateEmailErrorMessage,
  duplicateMobileErrorMessage,
} from '../../ContactDetails/Utils/formValidation'
import { isEmailValid, isMobileValid } from '@mobi/account/Utils/validation'
import { defer } from '@mobi/utils/functions'

interface PersonalDetailsProps {
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
}

export const PersonalDetails = ({ trackOptimoveEvent }: PersonalDetailsProps) => {
  const isAmlSignupRiskAssessmentEnabled = useHostContextFeature(
    FeatureFlags.AML_SIGNUP_RISK_ASSESSMENT.key
  )
  const navigate = useNavigate()
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)
  const state = useSelector(getSignUpState)
  const application = state.signupApplication
  const verification = state.signupVerification
  const dispatch = useDispatch()

  const [currentCountry, setCurrentCountry] = React.useState(Nationality.Australia)
  const [previousCountry, setPreviousCountry] = React.useState(Nationality.Australia)

  const isValidAddress =
    !!application.residentialAddress &&
    (!state.showPreviousAddress ||
      !!application.previousAddress ||
      previousCountry === Nationality.International)
  const isValidKnownByOtherNames =
    !state.isUserKnownByOtherNames ||
    isValidName(application.firstNameAlias) ||
    isValidName(application.lastNameAlias)

  const [focusedField, setFocusedField] = React.useState<string | null>(null)
  const [validationMessages, setValidationMessages] = React.useState<Record<string, string>>({})
  const { saveSignUpApplication, saveSignUpApplicationStatus } = useSignUpApplication({
    setValidationMessages,
    setShowErrorPopup,
  })
  const { applicationSave, applicationSaveStatus } = useApplication({
    setValidationMessages,
    setShowErrorPopup,
    nextPageRoute: routes.additionalInformation,
  })
  const [enterAddressMode, setEnterAddressMode] = React.useState<'Auto' | 'Manual'>('Auto')

  // #region helper functions
  function useQuery() {
    const params = new URLSearchParams()
    new URLSearchParams(useLocation().search).forEach((value, name) => {
      params.append(name.toLowerCase(), value)
    })
    return params
  }

  const queries = useQuery()

  function getSignupApplication(): TypeSignUpApplicationRequest {
    const dto = {
      firstName: application.firstName,
      middleName: application.middleName,
      surname: application.surname,
      birthdate: dayjs(application.birthdate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
      email: application.email,
      mobileNumber: application.mobileNumber,
      applicationReferenceNumber: application.applicationReferenceNumber,
      enquiryId: application.enquiryId,
      residentialAddress: application.residentialAddress,
      previousAddress: application.previousAddress,
      marketingOptInIndicator: application.marketingOptInIndicator,
      affiliateMarketingId: queries.get('affiliateid') as string,
      locationId: queries.get('locationid') as string,
      venueId: queries.get('venueid') as string,
      firstNameAlias: application.firstNameAlias,
      lastNameAlias: application.lastNameAlias,
    }
    return dto
  }

  type SaveSignUpResponse = ApplicationResponse | TypeSignUpApplicationResponse

  const isLegacySignUpResponse = (
    response: SaveSignUpResponse
  ): response is TypeSignUpApplicationResponse => 'isVerified' in response

  const onSubmit: FormEventHandler = e => {
    e.preventDefault()
    dispatch(setNumberOfAttemptsToVerify())

    if (!application.applicationReferenceNumber || !isAmlSignupRiskAssessmentEnabled) {
      const signupApplication = getSignupApplication()
      saveSignUpApplication(signupApplication, {
        onSuccess: response => {
          if (!isAmlSignupRiskAssessmentEnabled && isLegacySignUpResponse(response)) {
            const verification = response
            onSuccessLegacy(signupApplication, verification)
          }

          if (isAmlSignupRiskAssessmentEnabled) {
            dispatch(
              setSignupApplication({
                ...application,
                applicationReferenceNumber: Number(response.applicationReferenceNumber),
              })
            )
            dispatch(
              setSignupVerification({
                ...verification,
                duplicateExists: response.duplicateExists,
                isDuplicateClosed: response.isDuplicateClosed,
              })
            )
            if (response.duplicateExists) {
              navigate(routes.signUpUnsuccessful)
              return
            }
            navigate(routes.additionalInformation)
          }
        },
      })
    } else {
      applicationSave({})
    }
  }

  /* deprecated - to be removed once AML signup risk assessment is fully rolled out */
  const onSuccessLegacy = (
    application: TypeSignUpApplicationRequest,
    verification: TypeSignUpApplicationResponse
  ) => {
    const isSuccessful = isVerificationSuccessful(verification)

    dispatch(setSignupVerification(verification))
    dispatch(
      setSignupApplication({
        ...application,
        applicationReferenceNumber: verification.applicationReferenceNumber,
        enquiryId: verification.enquiryId,
      })
    )

    if (isSuccessful) {
      trackVerificationSuccess('Verified Address')
      trackOptimoveEvent(ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT)
      trackSignUpNameAndDOB(
        enterAddressMode,
        state.isUserKnownByOtherNames,
        application.marketingOptInIndicator
      )
      navigate(routes.password)
      return
    }

    if (verification.duplicateExists || verification.potentialSanctionedMatch) {
      navigate(routes.signUpUnsuccessful)
      return
    }

    if (!verification.isVerified) {
      trackSignUpError(location.pathname, 'user is not verified')
      navigate(routes.verificationOptions)
      return
    }
  }

  // #endregion

  const isOver18 = !!application.birthdate && !validationMessages.dateOfBirth
  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const isComplete =
    hasValidEntries &&
    application.birthdate.length > 0 &&
    isValidName(application.firstName) &&
    isValidName(application.middleName, true) &&
    isValidName(application.surname) &&
    isEmailValid(application.email) &&
    isMobileValid(application.mobileNumber) &&
    isValidAddress &&
    isValidKnownByOtherNames
  const isBusy = saveSignUpApplicationStatus === 'pending' || applicationSaveStatus === 'pending'

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  const validateIsKnownByOtherNames = () => {
    setValidationMessages(prev => ({
      ...prev,
      isKnownByOtherNames:
        state.isUserKnownByOtherNames &&
        !isValidName(application.firstNameAlias ?? '') &&
        !isValidName(application.lastNameAlias ?? '')
          ? 'Please enter details or untick the box.'
          : '',
    }))
  }

  return (
    <form name='contact-details-form' onSubmit={onSubmit}>
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            color='secondary_grey'
            onClick={() => {
              trackSignUpAlreadyHaveAccount(state.hasUserStartedSignup)
              navigate('/account')
            }}
            style={{ marginTop: spacing.md }}
            disabled={isBusy}
            type='button'
          >
            <span>
              Already have a <BoldStyled>TABtouch account?</BoldStyled>
            </span>
          </Button>
        </div>
        <AccountHeader
          alignItems='start'
          title='Join now and start betting!'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your full name and DOB as it appears on your ID documents (including middle name
              if applicable)
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <FormSection>
          <Legend>My details</Legend>
          <Input
            label='Date of Birth'
            name='dateOfBirth'
            placeholder='DD / MM / YYYY'
            required
            maxLength={10}
            value={application.birthdate}
            onChange={e =>
              dispatch(
                setSignupApplication({
                  ...application,
                  birthdate: getFormattedDateOfBirth(
                    e.target.value,
                    application.birthdate,
                    e.target.selectionStart
                  ),
                })
              )
            }
            onFocus={() => {
              resetFieldValidation('dateOfBirth')
              if (!state.hasUserStartedSignup) {
                trackSignUpStart()
                dispatch(setHasUserStartedSignup(true))
              }
              setFocusedField('dateOfBirth')
            }}
            onBlur={e => {
              if (focusedField === 'dateOfBirth') {
                // defer to ensure any onClick events are applied first before InfoBox is removed
                defer(() => {
                  setFocusedField(null)
                })
              }
              setValidationMessages(prev => ({
                ...prev,
                dateOfBirth: validateDateOfBirth(
                  getFormattedDateOfBirth(
                    e.target.value,
                    application.birthdate,
                    e.target.selectionStart
                  )
                ),
              }))
            }}
            isInvalid={!!validationMessages.dateOfBirth}
            helpText={
              validationMessages.dateOfBirth || 'You must be aged 18 or over to wager in Australia'
            }
            isDisabled={isBusy}
          />
          {focusedField === 'dateOfBirth' && (
            <InfoBox color='blue' size='sm'>
              Your DOB must match your ID documents
            </InfoBox>
          )}

          <NameInput
            firstName={application.firstName}
            middleName={application.middleName}
            lastName={application.surname}
            setFirstName={firstName =>
              dispatch(
                setSignupApplication({
                  ...application,
                  firstName,
                })
              )
            }
            setMiddleName={middleName =>
              dispatch(
                setSignupApplication({
                  ...application,
                  middleName,
                })
              )
            }
            setLastName={surname =>
              dispatch(
                setSignupApplication({
                  ...application,
                  surname,
                })
              )
            }
            isDisabled={isBusy || !isOver18}
          />

          {isAmlSignupRiskAssessmentEnabled && (
            <>
              <Checkbox
                id='sign-up-known-by-other-names'
                name='isKnownByOtherNames'
                checked={state.isUserKnownByOtherNames}
                label='Are you known by any other names?'
                onChange={e => {
                  dispatch(setUserKnownByOtherNames(e.target.checked))
                  dispatch(
                    setSignupApplication({
                      ...application,
                      firstNameAlias: '',
                      lastNameAlias: '',
                    })
                  )
                  resetFieldValidation('isKnownByOtherNames')
                }}
                isDisabled={isBusy || !isOver18}
              />

              {state.isUserKnownByOtherNames && (
                <>
                  <Input
                    type='text'
                    label='First name (if applicable)'
                    name='firstNameAlias'
                    placeholder='Enter first name'
                    value={application.firstNameAlias}
                    onChange={e =>
                      dispatch(
                        setSignupApplication({
                          ...application,
                          firstNameAlias: e.target.value,
                        })
                      )
                    }
                    onBlur={validateIsKnownByOtherNames}
                    isInvalid={!!validationMessages.isKnownByOtherNames}
                  />
                  <Input
                    type='text'
                    label='Last name (if applicable)'
                    name='lastNameAlias'
                    placeholder='Enter last name'
                    value={application.lastNameAlias}
                    onChange={e =>
                      dispatch(
                        setSignupApplication({
                          ...application,
                          lastNameAlias: e.target.value,
                        })
                      )
                    }
                    onBlur={validateIsKnownByOtherNames}
                    helpText={validationMessages.isKnownByOtherNames}
                    isInvalid={!!validationMessages.isKnownByOtherNames}
                  />
                </>
              )}
            </>
          )}

          <Input
            type='tel'
            label='Mobile'
            name='mobile'
            placeholder='Enter a valid mobile number'
            required
            maxLength={10}
            value={application.mobileNumber}
            onChange={e =>
              dispatch(
                setSignupApplication({
                  ...application,
                  mobileNumber: e.target.value.replace(/[\D]/gi, ''),
                })
              )
            }
            onFocus={() => resetFieldValidation('mobile')}
            onBlur={e =>
              setValidationMessages(prev => ({
                ...prev,
                mobile: getMobileValidationMessage(e.target.value),
              }))
            }
            isInvalid={!!validationMessages.mobile}
            helpText={
              validationMessages.mobile === 'duplicate'
                ? duplicateMobileErrorMessage
                : validationMessages.mobile
            }
            isDisabled={isBusy || !isOver18}
          />

          <Input
            type='email'
            label='Email'
            name='email'
            placeholder='Enter a valid email address'
            required
            maxLength={50}
            value={application.email}
            onChange={e =>
              dispatch(
                setSignupApplication({
                  ...application,
                  email: e.target.value,
                })
              )
            }
            onFocus={() => resetFieldValidation('email')}
            onBlur={e =>
              setValidationMessages(prev => ({
                ...prev,
                email: getEmailValidationMessage(e.target.value),
              }))
            }
            isInvalid={!!validationMessages.email}
            helpText={
              validationMessages.email === 'duplicate'
                ? duplicateEmailErrorMessage
                : validationMessages.email
            }
            isDisabled={isBusy || !isOver18}
          />

          {!state?.isVerificationAccount && (
            <div>
              <Checkbox
                id='sign-up-marketing-subscription'
                name='MarketingOptIn'
                checked={application.marketingOptInIndicator}
                label={
                  <CheckboxLabel>
                    I consent to receive advertising and promotional material by email, post, SMS,
                    telephone and other direct means in line with TABtouch’s{' '}
                    <a
                      href='https://www.rwwa.com.au/privacy-policy/'
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      Privacy Policy
                    </a>
                  </CheckboxLabel>
                }
                data-tid-sign-up-marketing-subscription=''
                onChange={e => {
                  dispatch(
                    setSignupApplication({
                      ...application,
                      marketingOptInIndicator: e.target.checked,
                    })
                  )
                }}
                isDisabled={isBusy || !isOver18}
              />
            </div>
          )}
        </FormSection>
        <FormSection>
          <Legend>
            <div>Current Address</div>
          </Legend>
          <CountrySelectField
            value={currentCountry}
            onChange={value => {
              setCurrentCountry(value)
              dispatch(
                setSignupApplication({
                  ...application,
                  residentialAddress: undefined,
                })
              )
            }}
            isDisabled={isBusy || !isOver18}
          />
          {currentCountry === Nationality.Australia && (
            <>
              <AddressInput
                prefix='current-address'
                onValidationChange={change => {
                  dispatch(
                    setSignupApplication({
                      ...application,
                      residentialAddress: change.isValid ? change.result : undefined,
                    })
                  )
                }}
                onEntryModeChanged={mode =>
                  setEnterAddressMode(mode === 'Manual' ? 'Manual' : 'Auto')
                }
                isDisabled={isBusy || !isOver18}
              />
              <Checkbox
                id='sign-up-2-years'
                label={
                  <CheckboxLabel>Have you lived at this address less than 2 years?</CheckboxLabel>
                }
                name='TwoYears'
                checked={state.showPreviousAddress}
                data-tid-sign-up-has-been-2-years=''
                onChange={e => {
                  dispatch(setShowPreviousAddress(e.target.checked))
                  dispatch(
                    setSignupApplication({
                      ...application,
                      previousAddress: undefined,
                    })
                  )
                  setPreviousCountry(Nationality.Australia)
                }}
                isDisabled={isBusy || !isOver18}
              />
              <Caption>
                Only a physical address can be used for verification purposes. You can change your
                postal address (including PO box addresses) once your account has been opened.
              </Caption>
              {state.showPreviousAddress && (
                <>
                  <Legend>
                    <div>Previous Address</div>
                  </Legend>
                  <CountrySelectField
                    value={previousCountry}
                    onChange={value => {
                      setPreviousCountry(value)
                      dispatch(
                        setSignupApplication({
                          ...application,
                          previousAddress: undefined,
                        })
                      )
                    }}
                    isDisabled={isBusy || !isOver18}
                  />
                  {previousCountry !== Nationality.International && (
                    <AddressInput
                      prefix='previous-address'
                      onValidationChange={change => {
                        dispatch(
                          setSignupApplication({
                            ...application,
                            previousAddress: change.isValid ? change.result : undefined,
                          })
                        )
                      }}
                      isDisabled={isBusy || !isOver18}
                    />
                  )}
                </>
              )}
            </>
          )}
          {currentCountry !== Nationality.Australia && (
            <InfoBox color='grey' size='md'>
              <span>Don't have an Australian address?</span>
              <div>
                We require an Australian residential address in order to progress your application.
                Please email us at <CustomerServiceEmail /> for assistance.
              </div>
            </InfoBox>
          )}
        </FormSection>
        <FooterStyled>
          {currentCountry === Nationality.Australia && (
            <>
              <Button
                type='submit'
                isFullWidth
                disabled={!isComplete || !isOver18}
                isLoading={isBusy}
              >
                Next
              </Button>
              <InfoBox color='grey' size='md' hideIcon={true}>
                <PersonalDetailsDisclaimer />
              </InfoBox>
            </>
          )}
        </FooterStyled>
      </PageLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </form>
  )
}
